import { useLocation, useHistory as useRouterHistory } from "react-router-dom";

export type LocationState = unknown;
export type Href = string;
export type Path = string;
export type Pathname = string;
export type Search = string;
export type Hash = string;
export type LocationKey = string;
export interface LocationDescriptorObject<State = LocationState> {
  pathname?: Pathname | undefined;
  search?: Search | undefined;
  state?: State | undefined;
  hash?: Hash | undefined;
  key?: LocationKey | undefined;
}
export type LocationDescriptor<State = LocationState> =
  | Path
  | LocationDescriptorObject<State>;
export interface Location<State = LocationState> {
  pathname: Pathname;
  search: Search;
  state: State;
  hash: Hash;
  key?: LocationKey | undefined;
}
export type Action = "PUSH" | "POP" | "REPLACE";
export type LocationListener<State = LocationState> = (
  location: Location<State>,
  action: Action
) => void;
export type UnregisterCallback = () => void;
export interface History<HistoryLocationState = LocationState> {
  length: number;
  action: Action;
  location: Location<HistoryLocationState>;
  push(
    location: Path | LocationDescriptor<HistoryLocationState>,
    state?: HistoryLocationState
  ): void;
  replace(
    location: Path | LocationDescriptor<HistoryLocationState>,
    state?: HistoryLocationState
  ): void;
  go(n: number): void;
  goBack(): void;
  goForward(): void;
  listen(listener: LocationListener<HistoryLocationState>): UnregisterCallback;
  createHref(
    location: Path | LocationDescriptorObject<HistoryLocationState>
  ): Href;
}

export function useHistory<
  HistoryLocationState = LocationState
>(): History<HistoryLocationState> {
  const internalHistory = useRouterHistory();
  const internalLocation = useLocation();

  const createHref = (
    location: Path | LocationDescriptorObject<HistoryLocationState>
  ): Href => {
    const query =
      typeof location === "string"
        ? (location.match(/(\?.+)$/)?.[0] ?? "").split("#")[0]
        : location.search;
    const newParams = new URLSearchParams(query);
    const existingParams = new URLSearchParams(internalLocation.search);

    for (const [key, value] of existingParams) {
      if (key.startsWith("utm_") && !newParams.get(value)) {
        newParams.append(key, value);
      }
    }

    if (typeof location === "string") {
      const to = location.split("?")[0] ?? "";
      return `${to}?${newParams.toString()}`;
    } else {
      const to = { ...location, search: newParams.toString() };
      return internalHistory.createHref(to);
    }
  };

  const navigate = (
    method: "push" | "replace",
    location: Path | LocationDescriptor<HistoryLocationState>,
    state?: HistoryLocationState
  ): void => {
    internalHistory[method](createHref(location), state);
  };

  const push = (
    location: Path | LocationDescriptor<HistoryLocationState>,
    state?: HistoryLocationState
  ): void => {
    navigate("push", location, state);
  };

  const replace = (
    location: Path | LocationDescriptor<HistoryLocationState>,
    state?: HistoryLocationState
  ): void => {
    navigate("replace", location, state);
  };

  return {
    ...internalHistory,
    createHref,
    push,
    replace,
  } as History<HistoryLocationState>;
}
